import { useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import { loadProject } from "../lib/projects";
import {
  getSiteUrl,
  SITE_DEFAULT_DESCRIPTION,
  SITE_OG_IMAGE_PATH,
} from "../lib/site";

function setMetaAttr(
  selector: string,
  attrs: Record<string, string>,
  createTag: "meta" | "link"
) {
  let el = document.head.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement(createTag);
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el!.setAttribute(k, v));
  }
}

const PERSON_JSON_LD_ID = "jsonld-person";

export function DocumentHead() {
  const { pathname } = useLocation();
  const { id } = useParams();

  useEffect(() => {
    const base = getSiteUrl();
    const canonicalPath = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
    const canonical = `${base}${canonicalPath === "/" ? "/" : canonicalPath}`;

    let title = "Alan Viles | engineering & projects";
    let description = SITE_DEFAULT_DESCRIPTION;
    let ogType: "website" | "article" = "website";

    if (pathname === "/projects") {
      title = "All projects | Alan Viles";
      description =
        "Portfolio of engineering and software projects: embedded systems, rockets, transport data, and more.";
    } else if (pathname.startsWith("/projects/") && id) {
      const project = loadProject(id);
      if (project) {
        title = `${project.title} | Alan Viles`;
        description = (project.brief ?? project.subtitle).replace(/\s+/g, " ").trim().slice(0, 320);
        ogType = "article";
      } else {
        title = "Project not found | Alan Viles";
        description = "That project is not in this portfolio. Browse all projects or return home.";
      }
    } else if (pathname === "/404") {
      title = "Page not found | Alan Viles";
      description = "This page is missing. Return home or browse projects.";
    }

    document.title = title;

    setMetaAttr('meta[name="description"]', { name: "description", content: description }, "meta");

    setMetaAttr('meta[property="og:title"]', { property: "og:title", content: title }, "meta");
    setMetaAttr(
      'meta[property="og:description"]',
      { property: "og:description", content: description },
      "meta"
    );
    setMetaAttr('meta[property="og:url"]', { property: "og:url", content: canonical }, "meta");
    setMetaAttr('meta[property="og:type"]', { property: "og:type", content: ogType }, "meta");
    setMetaAttr(
      'meta[property="og:image"]',
      { property: "og:image", content: `${base}${SITE_OG_IMAGE_PATH}` },
      "meta"
    );
    setMetaAttr(
      'meta[property="og:locale"]',
      { property: "og:locale", content: "en_NZ" },
      "meta"
    );

    setMetaAttr('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }, "meta");
    setMetaAttr('meta[name="twitter:title"]', { name: "twitter:title", content: title }, "meta");
    setMetaAttr(
      'meta[name="twitter:description"]',
      { name: "twitter:description", content: description },
      "meta"
    );
    setMetaAttr(
      'meta[name="twitter:image"]',
      { name: "twitter:image", content: `${base}${SITE_OG_IMAGE_PATH}` },
      "meta"
    );

    setMetaAttr('link[rel="canonical"]', { rel: "canonical", href: canonical }, "link");

    const existingLd = document.getElementById(PERSON_JSON_LD_ID);
    if (pathname === "/") {
      const personLd = {
        "@context": "https://schema.org",
        "@type": "Person",
        name: "Alan Viles",
        url: base,
        email: "alan@viles.nz",
        jobTitle: "Engineering student",
        description: SITE_DEFAULT_DESCRIPTION,
        sameAs: [
          "https://github.com/alanviles",
          "https://www.linkedin.com/in/alan-viles/",
          "https://alanvilesphotography.com/",
        ],
      };
      const serialized = JSON.stringify(personLd);
      let script = existingLd instanceof HTMLScriptElement ? existingLd : null;
      if (!script) {
        existingLd?.remove();
        script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = PERSON_JSON_LD_ID;
        document.head.appendChild(script);
      }
      script.textContent = serialized;
    } else {
      existingLd?.remove();
    }
  }, [pathname, id]);

  return null;
}
